import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaShieldAlt, FaLeaf, FaClock, FaUsers, FaLightbulb, FaBookOpen, FaGavel } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import allergiesImage from '../../components/assets/Staff/ALLERGIES1.jpg';

const Allergies = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const historyRef = useRef(null);
  const researchRef = useRef(null);
  const legalRef = useRef(null);
  const reminderRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'history': historyRef,
      'research': researchRef,
      'legal': legalRef,
      'reminder': reminderRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'history', ref: historyRef },
        { id: 'research', ref: researchRef },
        { id: 'legal', ref: legalRef },
        { id: 'reminder', ref: reminderRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Holistic Perspective', icon: FaHeart },
    { id: 'history', label: 'Background', icon: FaBookOpen },
    { id: 'research', label: 'Research & Evidence', icon: FaShieldAlt },
    { id: 'legal', label: 'Legal Context', icon: FaGavel },
    { id: 'reminder', label: 'Important Notes', icon: FaLightbulb }
  ];

  const overviewFeatures = [
    {
      icon: FaHeart,
      title: "Balance & Immune Resilience",
      description: "Supporting balance and helping reduce the overall stress burden on the body"
    },
    {
      icon: FaShieldAlt,
      title: "Non-Invasive & Complementary",
      description: "Frequency-based method applied within complementary wellness settings"
    },
    {
      icon: FaLeaf,
      title: "Personalised Approach",
      description: "Not used to diagnose or treat allergies; supports how the body responds to its environment"
    }
  ];

  const allergens = [
    "Pollen",
    "Dust mites",
    "Animal dander",
    "Mould",
    "Insect venom",
    "Certain foods",
    "Medications",
    "Chemicals commonly found in the environment"
  ];

  const researchStudies = [
    {
      researcher: "Dr Schumacher (1990)",
      study: "Observational study with 204 children with various allergic conditions",
      duration: "After five to nine months of sessions using a bioresonance-based approach",
      results: [
        "83% of participants no longer reported symptoms",
        "11% reported noticeable improvement",
        "Less than 5% reported no change"
      ],
      followUp: "In a follow-up observation focusing on individuals with hay fever, over 90% were reported to have experienced improvement or full relief of symptoms."
    },
    {
      researcher: "Dr Hennecke (1993)",
      study: "Meridian-based bioresonance approach",
      participants: "200 individuals experiencing eczema, allergic rhinitis, digestive sensitivities, and skin or respiratory complaints",
      results: [
        "50.4% of participants reporting no further symptoms",
        "34.1% reporting improvement"
      ],
      note: "Notably, this frequency-based method did not require allergen avoidance during the observation period."
    }
  ];

  const researchSources = [
    {
      title: "Healthline – overview of bioresonance and allergy-related research",
      url: "https://www.healthline.com/health/bioresonance#research"
    },
    {
      title: "The Open Epidemiology Journal (2020) – overview of complementary bioresonance research",
      url: "https://benthamopen.com/FULLTEXT/TOEPIJ-8-1"
    },
    {
      title: "NHS – general information on allergies",
      url: "https://www.nhs.uk/conditions/allergies/"
    }
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="Bioresonance for Allergies" description="Bioresonance therapy for allergies and sensitivities at Bioresonance Leicester. Complementary support for immune balance. Book at 108 New Walk." path="/therapy/bioresonance-therapy/allergies" />
        {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
          <ol className="flex items-center space-x-2 text-sm text-dark-grey">
            <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
            <li className="text-gold">/</li>
            <li><Link to="/therapy" className="hover:text-gold transition-colors">Therapy</Link></li>
            <li className="text-gold">/</li>
            <li><Link to="/therapy/bioresonance-therapy" className="hover:text-gold transition-colors">Bioresonance Therapy</Link></li>
            <li className="text-gold">/</li>
            <li className="text-gold font-semibold">Allergies</li>
          </ol>
        </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHeart className="mr-2" />
                Bioresonance Sub-Treatment
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                ALLERGIES
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed font-semibold">
                Supporting balance and immune resilience in a complementary way
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                For many people, allergies are not just seasonal discomfort. They can affect everyday life in subtle but persistent ways — constant reactions, sensitivity to foods or environmental factors, skin irritation, digestive discomfort, or the feeling that the immune system is always "on edge".
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Often, individuals living with allergies manage symptoms for years while continuing to search for additional, non-invasive ways to support their overall wellbeing alongside conventional medical care.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                This is where some people begin to explore complementary, non-pharmacological approaches, including bioresonance applied as a frequency-based method.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>60-90 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={allergiesImage}
                      alt="Allergies treatment"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              return (
                <button
                  type="button"
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                    activeSection === section.id
                      ? 'bg-gold text-black shadow-lg transform scale-105'
                      : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                  }`}
                  aria-current={activeSection === section.id ? 'location' : undefined}
                >
                  <SectionIcon className="mr-2 text-sm" />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>
      </section>

      {/* Understanding allergies from a holistic perspective */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Understanding allergies from a holistic perspective
              </h2>
              <p className="text-lg text-gray-600">
                From a holistic perspective, allergies are often viewed not as an isolated issue, but as part of a broader picture involving immune balance, environmental exposure, lifestyle factors and the overall stress load placed on the body.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                  >
                    <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-2xl text-gold" />
                    </div>
                    <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20 mb-8">
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                An allergy occurs when the immune system reacts to substances that are usually harmless. These substances, known as allergens, may include:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {allergens.map((allergen, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-gold rounded-full flex-shrink-0"></div>
                    <span className="text-gray-600">{allergen}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                Within complementary wellness settings, this frequency-based approach is not used to diagnose or treat allergies. Instead, it is applied to support balance and help reduce the overall stress burden that may influence how the body responds to its environment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Background: frequency-based methods and allergies */}
      <section ref={historyRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Background: frequency-based methods and allergies
              </h2>
              <p className="text-lg text-gray-600">
                Bioresonance methodology was originally developed in Germany in the 1970s by Erich Rasche and Dr Franz Morell, with early applications focusing primarily on allergies.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  This historical association is one of the reasons why allergies remain one of the most frequently discussed areas in bioresonance-related research and practitioner reports.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Over the past decades, a number of observational studies and clinical reports have explored the use of frequency-based bioresonance methods in individuals experiencing allergic symptoms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Selected research and observational findings */}
      <section ref={researchRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Selected research and observational findings
              </h2>
              <p className="text-lg text-gray-600">
                One of the earlier and frequently cited observational studies was conducted by Dr Schumacher in 1990. Further observational research has explored frequency-based approaches in allergic conditions.
              </p>
            </div>

            <div className="space-y-8">
              {researchStudies.map((study, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20"
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <FaBookOpen className="text-xl text-gold" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-dark-grey mb-2">{study.researcher}</h3>
                      <p className="text-lg text-gray-600 mb-2">{study.study}</p>
                      {study.duration && (
                        <p className="text-sm text-gray-500 mb-4">{study.duration}</p>
                      )}
                      {study.participants && (
                        <p className="text-sm text-gray-500 mb-4">{study.participants}</p>
                      )}
                    </div>
                  </div>

                  <div className="ml-16">
                    <h4 className="text-lg font-semibold text-dark-grey mb-3">Results:</h4>
                    <ul className="space-y-2 mb-4">
                      {study.results.map((result, resultIndex) => (
                        <li key={resultIndex} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-gold rounded-full flex-shrink-0"></div>
                          <span className="text-gray-600">{result}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {study.followUp && (
                      <div className="bg-gold/10 rounded-lg p-4 border border-gold/20">
                        <p className="text-gray-600"><strong>Follow-up:</strong> {study.followUp}</p>
                      </div>
                    )}
                    
                    {study.note && (
                      <div className="bg-blue/10 rounded-lg p-4 border border-blue/20 mt-4">
                        <p className="text-gray-600"><strong>Note:</strong> {study.note}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-gold/20">
              <p className="text-gray-600 leading-relaxed">
                It is important to emphasise that these findings are based on observational data and practitioner-reported outcomes. They do not constitute conclusive scientific proof, and results may vary significantly between individuals.
              </p>
            </div>

            {/* Evidence, research and further reading */}
            <div className="mt-12">
              {/* Header */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center mb-16"
              >
                <div className="inline-flex items-center bg-gradient-to-r from-gold to-yellow text-white px-8 py-4 rounded-full text-sm font-semibold mb-6 shadow-lg">
                  <FaBookOpen className="mr-3 text-lg" />
                  Evidence, research and further reading
                </div>
                <h3 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                  Evidence, research and further reading
                </h3>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                  Selected sources for overview of bioresonance and allergy-related research, and general information on allergies
                </p>
              </motion.div>
              
              {/* Enhanced Timeline */}
              <div className="relative max-w-5xl mx-auto">
                {/* Animated Timeline Line */}
                <motion.div 
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="absolute left-10 top-0 bottom-0 w-1 bg-gradient-to-b from-gold via-yellow to-gold rounded-full"
                ></motion.div>
                
                {/* Sources */}
                <div className="space-y-12">
                  {researchSources.map((source, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 + index * 0.08 }}
                      whileHover={{ x: 5 }}
                      className="relative flex items-start group"
                    >
                      {/* Enhanced Timeline Dot */}
                      <div className="relative z-10">
                        <motion.div 
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3, delay: 0.3 + index * 0.08 }}
                          whileHover={{ scale: 1.05 }}
                          className="w-20 h-20 bg-gradient-to-br from-gold to-yellow rounded-full flex items-center justify-center shadow-xl border-4 border-white"
                        >
                          <span className="text-white font-bold text-xl">{index + 1}</span>
                        </motion.div>
                        
                        {/* Glow Effect */}
                        <div className="absolute inset-0 w-20 h-20 bg-gold/30 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                      </div>
                      
                      {/* Enhanced Content Card */}
                      <div className="ml-12 flex-1">
                        <motion.div 
                          whileHover={{ y: -3 }}
                          className="bg-white rounded-2xl p-8 shadow-lg border border-grey/10 hover:shadow-2xl transition-all duration-200 group/card"
                        >
                          {/* Card Header */}
                          <div className="flex items-start justify-between mb-4">
                            <h4 className="text-2xl font-bold text-dark-grey group-hover/card:text-gold transition-colors duration-200 leading-tight">
                              {source.title}
                            </h4>
                            
                            {/* Enhanced External Link Button */}
                            <motion.div 
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.98 }}
                              className="flex-shrink-0"
                            >
                              <a 
                                href={source.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-12 h-12 bg-gradient-to-br from-gold/10 to-yellow/10 hover:from-gold hover:to-yellow rounded-full flex items-center justify-center transition-all duration-200 group/link"
                              >
                                <svg className="w-5 h-5 text-gold group-hover/link:text-white transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            </motion.div>
                          </div>
                          
                          {/* URL */}
                          <div className="bg-gray-50 rounded-lg p-4 border border-grey/20">
                            <a 
                              href={source.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue hover:text-gold transition-colors duration-200 text-sm break-all font-mono"
                            >
                              {source.url}
                            </a>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* Enhanced Footer */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.8 }}
                className="mt-16 text-center"
              >
                <div className="inline-flex items-center bg-gradient-to-r from-gold/10 to-yellow/10 text-gold px-8 py-4 rounded-full text-sm font-semibold shadow-lg border border-gold/20">
                  <FaShieldAlt className="mr-3 text-lg" />
                  All links open in new tab for your convenience
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal and professional context */}
      <section ref={legalRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Legal and professional context
              </h2>
              <p className="text-lg text-gray-600">
                On 14 May 2009, the Munich Higher Regional Court issued a ruling confirming the legitimacy of using the BICOM® bioresonance device in allergy-related applications within complementary practice in Germany.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaGavel className="text-2xl text-gold" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-4">Munich Higher Regional Court (2009)</h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-4">
                    On 14 May 2009, the Munich Higher Regional Court issued a ruling (case number 6U 2187/06) confirming the legitimacy of using the BICOM® bioresonance device in allergy-related applications within complementary practice in Germany.
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    The ruling did not classify this approach as a medical treatment, but strengthened its legal standing as a complementary method.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important considerations & In summary */}
      <section ref={reminderRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-blue/10 text-blue px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaLightbulb className="mr-2" />
                Important considerations
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Important considerations
              </h2>
              <p className="text-lg text-gray-600">
                This frequency-based approach does not diagnose allergies, does not replace allergy testing, and does not substitute medical treatment or prescribed medication.
              </p>
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20 mb-8">
              <div className="space-y-6">
                <p className="text-gray-600 leading-relaxed">
                  Anyone undergoing conventional medical care should consult their healthcare provider before starting any complementary method. Individual responses vary, and no specific outcome can be guaranteed.
                </p>
              </div>
            </div>

            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                In summary
              </h3>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
              <p className="text-lg text-gray-600 leading-relaxed">
                Bioresonance, applied as a frequency-based method using the BICOM® system, offers a non-invasive and personalised complementary option that some individuals choose to explore as part of a broader strategy to support balance and immune resilience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Explore Complementary Support for Allergies?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Bioresonance, applied as a frequency-based method using the BICOM® system, offers a non-invasive and personalised complementary option to support balance and immune resilience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Consultation
            </a>
          <Link 
            to="/therapy/bioresonance-therapy"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
          >
              Back to Bioresonance Therapy
          </Link>
        </div>
      </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default Allergies;
